<template>
  <SmTable
    :data="rows"
    :columns="rowColumns"
    :loading="loading"
    :default-sorting="defaultSorting"
    expandable
    @row-expand="handleRowExpand"
    key-field="software_name"
    class="flex-1">
    <template #software_name="{ row }">
      <TextMultiline :text="row.software_name" />
    </template>

    <template #booking_entries="{ row }">
      <TextMultiline :text="String(row.booking_entries ?? 0)" />
    </template>

    <template #last_booking_date="{ row }">
      <TextMultiline :text="formatLastBookingDate(row.last_booking_date)" />
    </template>

    <template #total_spends="{ row }">
      <TextMultiline :text="formatSpends(row.total_spends)" />
    </template>

    <template #expand="{ row }">
      <div
        class="w-full border-t border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
        <div v-if="expandState(row.software_name)?.loading" class="space-y-2">
          <div
            class="shadow-sm flex justify-between rounded border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900">
            <SmSkeleton height="16px" width="45%" />
            <SmSkeleton height="16px" width="30%" />
            <SmSkeleton height="16px" width="15%" />
          </div>
        </div>
        <div
          v-else-if="
            expandState(row.software_name)?.rows &&
            expandState(row.software_name)?.rows.length
          "
          class="space-y-2 text-sm">
          <div
            v-for="detail in expandState(row.software_name)?.rows"
            :key="detail._id"
            class="shadow-sm rounded border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
            <div
              class="grid items-center gap-3"
              style="
                grid-template-columns:
                  minmax(0, 3fr) minmax(120px, 1fr) minmax(150px, 1fr)
                  minmax(120px, 1fr);
              ">
              <div class="min-w-0">
                <TextMultiline class="font-semibold text-contrast">
                  {{ detail.row_content?.description || i18n.t('noData') }}
                </TextMultiline>
              </div>
              <p>
                {{ getBookingDate(detail) }}
              </p>

              <div class="flex items-center gap-2">
                <TextMultiline>
                  {{
                    detail.ai_prediction
                      ? i18n.t('aiDetected')
                      : i18n.t('rulebased')
                  }}
                </TextMultiline>

                <SmTag
                  v-if="aiPredictionEnabled"
                  appearance="default"
                  :label="`${getPredictionScore(detail)}%`"
                  :color="getAIPredictionColor(getPredictionScore(detail))"
                  hide-tooltip />
              </div>
              <div class="text-right text-sm font-semibold">
                {{
                  formatSpends(
                    detail.row_content?.cost ?? detail.cost,
                    (detail.currency as CurrencyCode | null | undefined) ??
                      (detail.row_content?.currency as
                        | CurrencyCode
                        | null
                        | undefined)
                  )
                }}
              </div>
            </div>
          </div>
        </div>
        <div v-else class="space-y-2">
          <div
            class="shadow-sm flex justify-between rounded border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900">
            <SmSkeleton height="16px" width="45%" />
            <SmSkeleton height="16px" width="30%" />
            <SmSkeleton height="16px" width="15%" />
          </div>
        </div>
      </div>
    </template>

    <template #no-data>
      <div class="py-8 text-center text-[color:var(--sm-contrast-muted)]">
        <p>{{ i18n.t('noData') }}</p>
      </div>
    </template>
  </SmTable>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'

  import {
    AccountingImportRow,
    AccountingImportStatisticsSoftwareSummary,
    CurrencyCode,
  } from '@/client'
  import { getCorrectedPrice } from '@/utils/formatter/price'
  import SmTable from '@/components/sm/SmTable.vue'
  import { Column, Sorting } from '@/components/sm/SmTable.types'
  import TextMultiline from '@/components/sm/TextMultiline.vue'
  import SmTag from '@/components/sm/SmTag.vue'
  import SmSkeleton from '@/components/sm/SmSkeleton.vue'
  import { capitalize } from '@/utils/formatter/formatter'
  import { displayDate } from '@/utils/formatter/date'

  interface Props {
    rows: AccountingImportStatisticsSoftwareSummary[]
    loading: boolean
    totalCount?: number
    detailsState?: Record<
      string,
      { loading?: boolean; rows?: AccountingImportRow[]; loaded?: boolean }
    >
    onExpandRow?: (softwareName: string) => void
    targetCurrencyCode?: CurrencyCode | null
    aiPredictionEnabled?: boolean
  }

  const props = defineProps<Props>()

  const i18n = useI18n()
  const aiPredictionEnabled = computed(
    () => props.aiPredictionEnabled !== false
  )
  const defaultSorting: Sorting = {
    by: 'software_name',
    asc: true,
  }

  const rowColumns = computed<
    Column<AccountingImportStatisticsSoftwareSummary>[]
  >(() => [
    {
      key: 'software_name',
      label: capitalize(i18n.t('application')),
      width: 2,
      loader: true,
      footer: totalApplicationsFooter,
      sortFn: function sortBySoftwareName(a, b) {
        return a.software_name.localeCompare(b.software_name)
      },
    },
    {
      key: 'booking_entries',
      loader: true,
      label: i18n.t('detectedBookings'),
      width: 1,
      sortFn: function sortByBookings(a, b) {
        return a.booking_entries - b.booking_entries
      },
      footer: totalDetectedBookings,
    },
    {
      key: 'last_booking_date',
      loader: true,
      label: i18n.t('lastBookingDate'),
      width: 1,
      sortFn: function sortByLastBookingDate(a, b) {
        return (
          new Date(a.last_booking_date ?? 0).getTime() -
          new Date(b.last_booking_date ?? 0).getTime()
        )
      },
    },
    {
      key: 'total_spends',
      label: capitalize(i18n.t('detectedVolume')),
      width: 1,
      sortFn: function sortByCosts(a, b) {
        return a.total_spends - b.total_spends
      },
      align: 'right',
      footer: totalSpendsFooter,
    },
  ])

  function handleRowExpand(
    row: AccountingImportStatisticsSoftwareSummary,
    expanded: boolean
  ) {
    if (!props.onExpandRow || !expanded) return
    props.onExpandRow(row.software_name)
  }

  function expandState(name: string): {
    loading: boolean
    loaded: boolean
    rows: AccountingImportRow[]
  } {
    const state = props.detailsState?.[name]
    return {
      loading: state?.loading ?? false,
      loaded: state?.loaded ?? false,
      rows: state?.rows ?? [],
    }
  }

  const totalApplicationsFooter = computed((): string => {
    const filtered = props.rows.length
    const total = props.totalCount ?? filtered
    return `${filtered} / ${total} ${i18n.t('applications')}`
  })

  const totalDetectedBookings = computed(() => {
    return props.rows.reduce((sum, row) => {
      return sum + (row.booking_entries ?? 0)
    }, 0)
  })

  const totalSpendsFooter = computed(() => {
    const sum = props.rows.reduce(
      (acc, row) => acc + (row.total_spends ?? 0),
      0
    )
    return formatSpends(sum)
  })

  function formatSpends(
    value: number | string | null | undefined,
    currency?: CurrencyCode | null
  ): string {
    if (value === null || value === undefined) return '—'
    const numeric = typeof value === 'string' ? Number(value) : value
    if (Number.isNaN(numeric)) return String(value)
    return (
      getCorrectedPrice(
        numeric,
        currency ?? props.targetCurrencyCode ?? undefined
      ) ?? numeric.toString()
    )
  }

  function getBookingDate(detail: AccountingImportRow): string {
    const value = detail.booking_date ?? detail.row_content?.booking_date
    if (!value) return '—'
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return String(value)
    return displayDate(parsed, { dateStyle: 'long' })
  }

  function getAIPredictionColor(
    certainty: number | string | null | undefined
  ): string {
    const score = Number(certainty) || 0
    if (score < 30) return '#f44336'
    if (score <= 80) return '#ff9800'
    return '#4caf50'
  }

  function getPredictionScore(detail: AccountingImportRow): number {
    if (detail.ai_prediction) {
      const value = Number(detail.prediction_certainty)
      return Number.isFinite(value) ? value : 0
    }
    return 95
  }

  function formatLastBookingDate(value?: string | null): string {
    if (!value) return '—'
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return String(value)
    return displayDate(parsed, { dateStyle: 'long' })
  }
</script>

<style scoped>
  th:last-of-type,
  td:last-of-type {
    text-align: right;
  }
</style>
